/**
 * EntitySyncPanel
 *
 * Generic import / export strip that replicates the IntegrationSyncPanel UX
 * for every entity type defined in importConfigs.ts.
 *
 * Tiles shown:
 *   - Excel / CSV — always (Import, Template ↓, Export)
 *   - Miro  — when the workspace admin has enabled it in connector_configs
 *   - Jira  — when the workspace admin has enabled it in connector_configs
 *
 * Connector tile states (per connectorConfigsService):
 *   disabled  → tile hidden
 *   available → tile shows "Connect" button linking to /prioritization/settings/connectors
 *   active    → tile shows action buttons (Import / Export)
 *
 * Usage:
 *   <EntitySyncPanel
 *     entityType="outcome_drivers"
 *     data={drivers}
 *     filename="outcome-drivers"
 *     onImport={async (rows) => { ... }}
 *   />
 */

import { useState, useEffect, useRef } from 'react';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import {
  RefreshCw,
  FileSpreadsheet,
  ArrowDownToLine,
  ArrowUpFromLine,
  Download,
  ChevronDown,
  FileDown,
  Plug,
  X,
  Loader2,
  ExternalLink,
  Check,
} from 'lucide-react';
import { ExcelImportModal } from '../prioritization/import-export/ExcelImportModal';
import { IMPORT_CONFIGS, type ImportEntityType, type ImportConfig } from '../../lib/importConfigs';
import { exportToCsv } from '../../lib/reportExport';
import { exportToExcel } from '../../lib/excelService';
import { resolveConnectorStatuses, resolveConnectorConfig, type ConnectorStatus, type ConnectorType } from '../../lib/connectorConfigsService';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { registerImportedRecords, type ImportedRecordIdentity } from '../../lib/semanticConnectors/importIdentity';
import type { ChangeEventEntityType } from '../../lib/connectors';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getIntegrationsApi, formatIntegrationsNetworkError, type MiroBoard, type NotionDatabaseSummary } from '../../lib/integrationsApi';
import { loadMiroColorMaps, getColorRulesForEntity, resolveColorField, loadFieldMappingConfig, type FieldMappingSet } from '../../lib/fieldMappingRegistry';
import { applyMappingSet, resolveActiveMappingSet, type ActiveMappingSet } from '../../lib/fieldMappingApply';
import { MappingSetNotice } from '../prioritization/connectors/MappingSetNotice';

// ── Template helper ────────────────────────────────────────────────────────────

/**
 * Generates and downloads an Excel template file for the given import config.
 * Contains one header row, one example row, and an Instructions sheet.
 */
function downloadEntityTemplate(config: ImportConfig, filename: string): void {
  const headers = config.appFields.map((f) => f.key);

  const exampleRow = Object.fromEntries(
    config.appFields.map((f) => [
      f.key,
      f.required ? `Example ${f.label.toLowerCase()}` : '',
    ])
  );

  const ws = XLSX.utils.json_to_sheet([exampleRow], { header: headers });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Data');

  const instructions: (string | number)[][] = [
    [`${config.label} — import template`],
    [],
    [config.description],
    [],
    ['Column', 'Required', 'Type', 'Description'],
    ...config.appFields.map((f) => [f.key, f.required ? 'Yes' : 'No', f.type ?? 'string', f.label]),
  ];
  const wsInstr = XLSX.utils.aoa_to_sheet(instructions);
  wsInstr['!cols'] = [{ wch: 30 }, { wch: 10 }, { wch: 12 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

  XLSX.writeFile(wb, `${filename}_template.xlsx`);
}

// ── Props ──────────────────────────────────────────────────────────────────────

/**
 * A created entity's external identity, returned from onImport so the panel
 * can register it on the sync fabric (set 5B convergence). Optional — a host
 * that returns void gets no registration, preserving the old contract.
 */
export interface ImportedEntityRef {
  externalId: string;
  internalId: string;
  externalUrl?: string;
  label?: string;
}

export interface EntitySyncPanelProps {
  entityType: ImportEntityType;
  /** Current rows used for export. Provide either this or getData. */
  data?: Record<string, unknown>[];
  /** Async getter for export when data isn't already in memory. */
  getData?: () => Promise<Record<string, unknown>[]>;
  /** Base filename for exports and template download (no extension). */
  filename: string;
  /**
   * Called after the user completes the import wizard. A host that creates
   * entities may return their {externalId, internalId} refs so the panel
   * registers external identity + a change event (sync-fabric convergence).
   * Returning void keeps the legacy behavior.
   */
  onImport: (rows: Record<string, unknown>[]) => Promise<void | ImportedEntityRef[]> | void | ImportedEntityRef[];
  /** Hide the export controls (template download still available). */
  hideExport?: boolean;
}

/**
 * ImportEntityType → the change_events/entity_external_ids entity_type. null =
 * no fabric identity for that entity kind.
 */
const IMPORT_ENTITY_TO_FABRIC: Record<ImportEntityType, ChangeEventEntityType | null> = {
  items: 'item',
  outcome_drivers: 'outcome_driver',
  backplan_nodes: 'backplan_node',
  roadmap_nodes: 'roadmap_node',
  open_questions: 'open_question',
};

// ── Inline export dropdown ─────────────────────────────────────────────────────

function InlineExportMenu({
  data,
  getData,
  filename,
}: {
  data?: Record<string, unknown>[];
  getData?: () => Promise<Record<string, unknown>[]>;
  filename: string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  async function resolve(): Promise<Record<string, unknown>[]> {
    if (getData) return getData();
    return data ?? [];
  }

  async function handleCsv() {
    setLoading(true);
    try {
      const rows = await resolve();
      exportToCsv(rows, filename);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }

  async function handleExcel() {
    setLoading(true);
    try {
      const rows = await resolve();
      exportToExcel({
        filename,
        sheets: [{ name: 'Data', data: rows }],
      });
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }

  async function handleJson() {
    setLoading(true);
    try {
      const rows = await resolve();
      const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
      setOpen(false);
    }
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={loading}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
      >
        {loading ? (
          <RefreshCw className="h-3 w-3 animate-spin" />
        ) : (
          <ArrowUpFromLine className="h-3 w-3" />
        )}
        Export
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] shadow-lg z-50 py-1">
          <button
            onClick={handleCsv}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface)]"
          >
            <FileDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            CSV
          </button>
          <button
            onClick={handleExcel}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface)]"
          >
            <FileSpreadsheet className="h-3.5 w-3.5 text-[var(--green)]" />
            Excel
          </button>
          <button
            onClick={handleJson}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--surface)]"
          >
            <FileDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            JSON
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

const MIRO_SVG = (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 flex-shrink-0" fill="#FFD02F">
    <path d="M17.392 2H13.33l3.622 5.913L12.44 2H8.378l3.344 7.007L7.44 2H3.377L7.44 12 3.377 22h4.063l4.282-7.007L8.379 22h4.062l4.282-9.007L20.606 22h4.017L17.392 12l7.23-10H17.392z" />
  </svg>
);

const JIRA_SVG = (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 flex-shrink-0" fill="#0052CC">
    <path d="M11.571 11.429 5.714 5.571l5.857 5.858-5.857 5.857 5.857-5.857zm1.143-5.858L6.857 12l5.857 5.857L18.571 12l-5.857-5.857z" />
  </svg>
);

const NOTION_SVG = (
  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 flex-shrink-0">
    <rect width="24" height="24" rx="4" fill="#000000" />
    <path d="M6 6h8l4 4v8H6V6z" fill="white" />
    <path d="M8 10h6M8 13h4M8 16h5" stroke="black" strokeWidth="1" />
  </svg>
);

/**
 * Translate a Notion-imported item into a Priorities.ai row using the active
 * import mapping set configured on the Field Mappings page. Without a set (or an
 * empty one), falls back to title → title, page content → description.
 *
 * The engine returns `metadata` keyed by the actual Notion property name; a
 * mapping entry names that property via `externalFieldLabel` (the free-text
 * "enter property name" field), or by the registry key for title/content.
 * The rules run through the shared applier (lib/fieldMappingApply).
 */
function translateNotionRow(
  item: { title?: string; description?: string; metadata?: Record<string, unknown> },
  importSet: FieldMappingSet | null,
): Record<string, unknown> {
  const row: Record<string, unknown> = {
    title: item.title ?? '',
    description: item.description ?? '',
    // Preserve the origin so a host that supports it (items.external_id,
    // migration 392) carries a durable link back to the Notion record —
    // the minimum viable convergence for every import, regardless of host.
    ...((item as { externalId?: string }).externalId ? { external_id: (item as { externalId?: string }).externalId } : {}),
    ...((item as { externalUrl?: string }).externalUrl ? { external_url: (item as { externalUrl?: string }).externalUrl } : {}),
  };

  // External values keyed the way the Notion rules name them: the title and
  // page content by registry key, every other property by its Notion name.
  const external = { ...(item.metadata ?? {}), title: item.title, content: item.description };
  const applied = applyMappingSet(external, importSet);
  for (const mapped of [...applied.fields, ...applied.attributes]) {
    row[mapped.internalFieldKey] = mapped.value;
  }
  // Notion has always copied a value its rule's value list doesn't name in
  // as-is; kept here so this refactor changes no Notion import. A multi-select
  // reports each value its list doesn't name: when the list named none of
  // them they come back as one list; when it named some, the rule's mapped
  // values stand.
  const produced = new Set([...applied.fields, ...applied.attributes].map((m) => m.ruleId));
  const unnamed = new Map<string, { key: string; values: unknown[] }>();
  for (const miss of applied.unmatched) {
    if (produced.has(miss.ruleId)) continue;
    const entry = unnamed.get(miss.ruleId) ?? { key: miss.internalFieldKey, values: [] };
    entry.values.push(miss.externalValue);
    unnamed.set(miss.ruleId, entry);
  }
  for (const { key, values } of unnamed.values()) {
    row[key] = values.length === 1 ? values[0] : values;
  }

  return row;
}

function ConnectorTile({
  icon,
  label,
  status,
  onImport,
  onExport,
}: {
  icon: React.ReactNode;
  label: string;
  status: ConnectorStatus;
  onImport?: () => void;
  onExport?: () => void;
}) {
  if (status === 'disabled') return null;

  return (
    <div className="flex items-center gap-1.5">
      {icon}
      {status === 'available' ? (
        <Link
          to="/prioritization/settings/connectors"
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)]"
          title={`Connect ${label} in Settings`}
        >
          <Plug className="h-3 w-3" />
          Connect {label}
        </Link>
      ) : (
        <>
          {onImport && (
            <button
              onClick={onImport}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-pri)]"
              title={`Import from ${label}`}
            >
              <ArrowDownToLine className="h-3 w-3" />
              Import
            </button>
          )}
          {onExport && (
            <button
              onClick={onExport}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--rule)] bg-[var(--bg-raised)] hover:bg-[var(--surface)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-pri)]"
              title={`Export to ${label}`}
            >
              <ArrowUpFromLine className="h-3 w-3" />
              Export
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Miro modal ─────────────────────────────────────────────────────────────────

type MiroModalMode = 'import' | 'export';

function MiroSyncModal({
  mode: initialMode,
  onClose,
  onImport,
  exportData,
  exportFilename,
  entityType,
}: {
  mode: MiroModalMode;
  onClose: () => void;
  onImport: (rows: Record<string, unknown>[]) => void;
  exportData?: Record<string, unknown>[];
  exportFilename: string;
  entityType?: ImportEntityType;
}) {
  const [mode, setMode] = useState<MiroModalMode>(initialMode);
  const [connecting, setConnecting] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  // Import state
  const [boards, setBoards] = useState<MiroBoard[]>([]);
  const [selectedBoard, setSelectedBoard] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [colorRules, setColorRules] = useState<Array<{ color: string; field: string }>>([]);

  // Export state
  const [boardName, setBoardName] = useState(exportFilename || 'Priorities Export');
  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ boardUrl: string; count: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { userId } = useUserStore();
  const workspaceId = getCurrentWorkspaceId();

  // Auto-connect on mount using stored connector_configs credentials (§2.1)
  // Also load color rules for this entity type.
  useEffect(() => {
    async function autoConnect() {
      setConnecting(true);
      setConnectionError(null);
      try {
        if (!workspaceId) throw new Error('No active workspace');

        // Load color rules alongside credentials
        const [{ data: config }, colorMaps] = await Promise.all([
          resolveConnectorConfig(workspaceId, userId ?? null, 'miro' as ConnectorType),
          loadMiroColorMaps(workspaceId),
        ]);

        if (entityType) {
          setColorRules(getColorRulesForEntity(colorMaps, entityType));
        }

        const token = (config?.authData as Record<string, string> | undefined)?.accessToken;
        if (!token) {
          setConnectionError('Miro is not connected. Go to Settings → Connectors to add your Miro credentials.');
          return;
        }
        const api = getIntegrationsApi(workspaceId);
        const conn = await api.connectMiro(token);
        if (!conn.connected) {
          setConnectionError(
            conn.error?.trim() ||
              'Miro did not accept these credentials. Update them in Settings → Connectors.'
          );
          return;
        }
        const boardList = await api.listMiroBoards();
        setBoards(boardList);
        setConnected(true);
      } catch (err) {
        setConnectionError(formatIntegrationsNetworkError(err));
      } finally {
        setConnecting(false);
      }
    }
    autoConnect();
  }, [workspaceId, userId, entityType]);

  async function handleImport() {
    if (!selectedBoard) return;
    setImporting(true);
    setImportError(null);
    try {
      const api = getIntegrationsApi(workspaceId || 'default');
      const result = await api.importFromMiro(selectedBoard);

      // Apply stored color rules: each sticky note's fill color determines which
      // field its text lands in. Unmapped colors always go to `title`.
      const rows = result.items.map((item: {
        title?: string; text?: string; description?: string;
        fillColor?: string; color?: string; style?: { fillColor?: string };
      }) => {
        const noteText = item.title || item.text || '';
        const noteColor = item.fillColor || item.color || item.style?.fillColor;
        const targetField = resolveColorField(noteColor, colorRules);

        if (targetField === 'title' || colorRules.length === 0) {
          return { title: noteText, description: item.description || '' };
        }
        // Place text in the mapped field; leave title blank so the entity is still
        // valid (title-required validation happens at entity creation time)
        return { title: '', [targetField]: noteText, description: item.description || '' };
      });

      onImport(rows);
      onClose();
    } catch (err) {
      setImportError(formatIntegrationsNetworkError(err));
    } finally {
      setImporting(false);
    }
  }

  async function handleExport() {
    if (!boardName.trim() || !exportData?.length) return;
    setExporting(true);
    setExportError(null);
    try {
      const api = getIntegrationsApi(workspaceId || 'default');
      const items = exportData.map((row) => ({
        title: String(row.title || row.name || row.text || ''),
        description: row.description ? String(row.description) : undefined,
      })).filter((i) => i.title);
      const result = await api.exportItemsToMiro(boardName.trim(), items);
      setExportResult({ boardUrl: result.boardUrl, count: result.count });
    } catch (err) {
      setExportError(formatIntegrationsNetworkError(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
            <div className="flex items-center gap-2.5">
              <svg viewBox="0 0 24 24" className="h-5 w-5 flex-shrink-0" fill="#FFD02F">
                <path d="M17.392 2H13.33l3.622 5.913L12.44 2H8.378l3.344 7.007L7.44 2H3.377L7.44 12 3.377 22h4.063l4.282-7.007L8.379 22h4.062l4.282-9.007L20.606 22h4.017L17.392 12l7.23-10H17.392z" />
              </svg>
              <h2 className="text-base font-semibold text-[var(--text-pri)]">Miro Integration</h2>
            </div>
            <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-[var(--rule)] px-5">
            {(['import', 'export'] as MiroModalMode[]).map((t) => (
              <button
                key={t}
                onClick={() => setMode(t)}
                className={[
                  'py-2.5 px-3 text-sm font-medium border-b-2 transition-colors capitalize -mb-px',
                  mode === t
                    ? 'border-[var(--mustard)] text-[var(--mustard)] dark:text-[var(--mustard)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]',
                ].join(' ')}
              >
                {t === 'import' ? '↓ Import from Miro' : '↑ Export to Miro'}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {connecting && (
              <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting to Miro…
              </div>
            )}

            {!connecting && connectionError && (
              <div className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] px-3 py-2.5 space-y-2">
                <p>{connectionError}</p>
                <Link
                  to="/prioritization/settings/connectors"
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
                >
                  Go to Settings → Connectors
                </Link>
              </div>
            )}

            {!connecting && connected && mode === 'import' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                    Select a Miro board to import from
                  </label>
                  {boards.length === 0 ? (
                    <p className="text-sm text-[var(--text-ter)]">No boards found in your Miro account.</p>
                  ) : (
                    <select
                      value={selectedBoard}
                      onChange={(e) => setSelectedBoard(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                    >
                      <option value="">— Select a board —</option>
                      {boards.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  )}
                  {colorRules.length > 0 ? (
                    <div className="mt-1.5 text-xs text-[var(--text-ter)] space-y-0.5">
                      <p>Color rules active:</p>
                      {colorRules.map((r, i) => {
                        const MIRO_COLOR_HEX: Record<string, string> = {
                          yellow:'#FFD02F', light_yellow:'#F5F5DC', orange:'#FF9800',
                          light_green:'#90EE90', green:'#4CAF50', dark_green:'#2E7D32',
                          cyan:'#00BCD4', light_blue:'#87CEEB', blue:'#2196F3',
                          dark_blue:'#3F51B5', light_pink:'#FFCDD2', pink:'#E91E63',
                          violet:'#9C27B0', red:'#F44336', gray:'#9E9E9E', black:'#212121',
                        };
                        return (
                          <span key={i} className="inline-flex items-center gap-1 mr-2">
                            <span className="h-2.5 w-2.5 rounded-full border border-[var(--rule)] inline-block" style={{ background: MIRO_COLOR_HEX[r.color] ?? r.color }} />
                            {r.color} → {r.field}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-1.5 text-xs text-[var(--text-ter)]">
                      Sticky notes will be imported as items. Configure color mappings in Settings → Connectors to route colors to specific fields.
                    </p>
                  )}
                </div>
                {importError && (
                  <p className="text-sm text-[var(--coral)]">{importError}</p>
                )}
              </>
            )}

            {!connecting && connected && mode === 'export' && (
              <>
                {exportResult ? (
                  <div className="flex flex-col items-center gap-3 py-2 text-center">
                    <div className="h-10 w-10 rounded-full bg-[var(--green-dim)] dark:bg-[var(--green)]/30 flex items-center justify-center">
                      <Check className="h-5 w-5 text-[var(--green)]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[var(--text-pri)]">
                        {exportResult.count} item{exportResult.count !== 1 ? 's' : ''} exported
                      </p>
                      <a
                        href={exportResult.boardUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-sm text-[var(--mustard)] hover:underline"
                      >
                        Open board in Miro <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                        New board name
                      </label>
                      <input
                        type="text"
                        value={boardName}
                        onChange={(e) => setBoardName(e.target.value)}
                        placeholder="e.g. Q3 Priorities"
                        className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      />
                    </div>
                    {!exportData?.length && (
                      <p className="text-sm text-[var(--mustard)]">No data to export.</p>
                    )}
                    {exportError && (
                      <p className="text-sm text-[var(--coral)]">{exportError}</p>
                    )}
                    <p className="text-xs text-[var(--text-ter)]">
                      Creates a new Miro board with {exportData?.length ?? 0} sticky note{(exportData?.length ?? 0) !== 1 ? 's' : ''}.
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!connecting && connected && !exportResult && (
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              {mode === 'import' ? (
                <button
                  onClick={handleImport}
                  disabled={!selectedBoard || importing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--mustard)] hover:bg-[var(--mustard)] text-[var(--text-pri)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {importing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {importing ? 'Importing…' : 'Import Sticky Notes'}
                </button>
              ) : (
                <button
                  onClick={handleExport}
                  disabled={!boardName.trim() || !exportData?.length || exporting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--mustard)] hover:bg-[var(--mustard)] text-[var(--text-pri)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {exporting ? 'Creating board…' : 'Create Miro Board'}
                </button>
              )}
            </div>
          )}
          {(!connecting && connected && exportResult) && (
            <div className="flex justify-end px-5 pb-5">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] text-[var(--text-sec)] transition-colors">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ── Notion modal ────────────────────────────────────────────────────────────────

type NotionModalMode = 'import' | 'export';

function NotionSyncModal({
  mode: initialMode,
  onClose,
  onImport,
  exportData,
}: {
  mode: NotionModalMode;
  onClose: () => void;
  onImport: (rows: Record<string, unknown>[]) => void;
  exportData?: Record<string, unknown>[];
}) {
  const [mode, setMode] = useState<NotionModalMode>(initialMode);
  const [connecting, setConnecting] = useState(true);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const [databases, setDatabases] = useState<NotionDatabaseSummary[]>([]);
  const [selectedDb, setSelectedDb] = useState('');
  const [activeImportSet, setActiveImportSet] = useState<ActiveMappingSet>({ set: null, chosen: false });
  const importSet = activeImportSet.set;

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const [exporting, setExporting] = useState(false);
  const [exportResult, setExportResult] = useState<{ count: number } | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const { userId } = useUserStore();
  const workspaceId = getCurrentWorkspaceId();

  // Auto-connect: confirm stored credentials via the engine, list databases,
  // and load the active Notion import mapping set (Field Mappings page).
  useEffect(() => {
    async function autoConnect() {
      setConnecting(true);
      setConnectionError(null);
      try {
        if (!workspaceId) throw new Error('No active workspace');
        const api = getIntegrationsApi(workspaceId);

        const [status, config] = await Promise.all([
          api.getNotionStatus(userId ?? undefined),
          loadFieldMappingConfig(workspaceId, 'notion'),
        ]);

        setActiveImportSet(resolveActiveMappingSet(config, 'import'));

        if (!status.connected) {
          setConnectionError('Notion is not connected. Go to Settings → Connectors to connect it.');
          return;
        }

        const dbs = await api.listNotionDatabases(userId ?? undefined);
        setDatabases(dbs);
        setConnected(true);
      } catch (err) {
        setConnectionError(formatIntegrationsNetworkError(err));
      } finally {
        setConnecting(false);
      }
    }
    autoConnect();
  }, [workspaceId, userId]);

  async function handleImport() {
    if (!selectedDb) return;
    setImporting(true);
    setImportError(null);
    try {
      const api = getIntegrationsApi(workspaceId || 'default');
      const result = await api.importFromNotion(selectedDb, { userId: userId ?? undefined });
      const rows = result.items.map((item) => translateNotionRow(item, importSet));
      onImport(rows);
      onClose();
    } catch (err) {
      setImportError(formatIntegrationsNetworkError(err));
    } finally {
      setImporting(false);
    }
  }

  async function handleExport() {
    if (!selectedDb || !exportData?.length) return;
    setExporting(true);
    setExportError(null);
    try {
      const api = getIntegrationsApi(workspaceId || 'default');
      const priorities = exportData
        .map((row, i) => ({
          id: String(row.id ?? row.externalId ?? i),
          title: String(row.title || row.name || row.text || ''),
          description: row.description ? String(row.description) : undefined,
          rank: typeof row.rank === 'number' ? row.rank : i + 1,
          category: row.category ? String(row.category) : undefined,
        }))
        .filter((p) => p.title);
      const result = await api.exportToNotion(selectedDb, priorities, { userId: userId ?? undefined });
      setExportResult({ count: result.created.length });
    } catch (err) {
      setExportError(formatIntegrationsNetworkError(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)] w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)]">
            <div className="flex items-center gap-2.5">
              {NOTION_SVG}
              <h2 className="text-base font-semibold text-[var(--text-pri)]">Notion Integration</h2>
            </div>
            <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)] transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex border-b border-[var(--rule)] px-5">
            {(['import', 'export'] as NotionModalMode[]).map((t) => (
              <button
                key={t}
                onClick={() => setMode(t)}
                className={[
                  'py-2.5 px-3 text-sm font-medium border-b-2 transition-colors capitalize -mb-px',
                  mode === t
                    ? 'border-[var(--rule)] text-[var(--text-pri)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-ter)]',
                ].join(' ')}
              >
                {t === 'import' ? '↓ Import from Notion' : '↑ Export to Notion'}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="px-5 py-5 space-y-4">
            {connecting && (
              <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                <Loader2 className="h-4 w-4 animate-spin" />
                Connecting to Notion…
              </div>
            )}

            {!connecting && connectionError && (
              <div className="text-sm text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)] px-3 py-2.5 space-y-2">
                <p>{connectionError}</p>
                <Link
                  to="/prioritization/settings/connectors"
                  onClick={onClose}
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
                >
                  Go to Settings → Connectors
                </Link>
              </div>
            )}

            {!connecting && connected && (
              <div>
                <label className="block text-sm font-medium text-[var(--text-sec)] mb-1.5">
                  {mode === 'import' ? 'Import from database' : 'Export to database'}
                </label>
                {databases.length === 0 ? (
                  <p className="text-sm text-[var(--text-ter)]">
                    No databases are shared with this integration yet. Share a database with it in Notion.
                  </p>
                ) : (
                  <select
                    value={selectedDb}
                    onChange={(e) => setSelectedDb(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)] focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                  >
                    <option value="">— Select a database —</option>
                    {databases.map((d) => (
                      <option key={d.id} value={d.id}>{d.title}</option>
                    ))}
                  </select>
                )}

                {mode === 'import' && (
                  <MappingSetNotice
                    connectorType="notion"
                    active={activeImportSet}
                    checkAttributes={false}
                    className="mt-1.5"
                    emptyText={<>Title and page content map to title and description. Configure a mapping set under <span className="font-medium">Settings → Field Mappings</span> to route Notion properties to specific fields.</>}
                  />
                )}
                {mode === 'export' && (
                  <p className="mt-1.5 text-xs text-[var(--text-ter)]">
                    Creates {exportData?.length ?? 0} page{(exportData?.length ?? 0) !== 1 ? 's' : ''} in the selected database (title, description, rank, category where those properties exist).
                  </p>
                )}

                {importError && mode === 'import' && <p className="mt-2 text-sm text-[var(--coral)]">{importError}</p>}
                {exportError && mode === 'export' && <p className="mt-2 text-sm text-[var(--coral)]">{exportError}</p>}
                {mode === 'export' && !exportData?.length && (
                  <p className="mt-2 text-sm text-[var(--mustard)]">No data to export.</p>
                )}
                {exportResult && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                    <Check className="h-4 w-4" />
                    {exportResult.count} page{exportResult.count !== 1 ? 's' : ''} created in Notion.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          {!connecting && connected && !exportResult && (
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
              >
                Cancel
              </button>
              {mode === 'import' ? (
                <button
                  onClick={handleImport}
                  disabled={!selectedDb || importing}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--bg)] hover:bg-[var(--bg-raised)] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:hover:bg-[var(--bg-raised)]"
                >
                  {importing && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {importing ? 'Importing…' : 'Import Pages'}
                </button>
              ) : (
                <button
                  onClick={handleExport}
                  disabled={!selectedDb || !exportData?.length || exporting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--bg)] hover:bg-[var(--bg-raised)] text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors dark:hover:bg-[var(--bg-raised)]"
                >
                  {exporting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {exporting ? 'Exporting…' : 'Export to Notion'}
                </button>
              )}
            </div>
          )}
          {!connecting && connected && exportResult && (
            <div className="flex justify-end px-5 pb-5">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] text-[var(--text-sec)] transition-colors">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export function EntitySyncPanel({
  entityType,
  data,
  getData,
  filename,
  onImport,
  hideExport = false,
}: EntitySyncPanelProps) {
  const config = IMPORT_CONFIGS[entityType];
  const [showImport, setShowImport] = useState(false);
  const [miroModal, setMiroModal] = useState<'import' | 'export' | null>(null);
  const [notionModal, setNotionModal] = useState<'import' | 'export' | null>(null);
  /** Snapshot for Miro export when the panel uses getData instead of inline data. */
  const [miroExportRows, setMiroExportRows] = useState<Record<string, unknown>[] | null>(null);
  /** Snapshot for Notion export when the panel uses getData instead of inline data. */
  const [notionExportRows, setNotionExportRows] = useState<Record<string, unknown>[] | null>(null);
  const [connectorStatuses, setConnectorStatuses] = useState<Record<string, ConnectorStatus>>({
    miro: 'disabled',
    jira: 'disabled',
    notion: 'disabled',
  });

  const { userId } = useUserStore();
  const workspaceId = getCurrentWorkspaceId();

  // Load connector statuses from DB on mount
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    resolveConnectorStatuses(workspaceId, userId || null, ['miro', 'jira', 'notion']).then((statuses) => {
      if (!cancelled) setConnectorStatuses(statuses);
    });
    return () => { cancelled = true; };
  }, [workspaceId, userId]);

  const miroStatus = connectorStatuses['miro'] as ConnectorStatus;
  const jiraStatus = connectorStatuses['jira'] as ConnectorStatus;
  const notionStatus = connectorStatuses['notion'] as ConnectorStatus;
  const showConnectors = miroStatus !== 'disabled' || jiraStatus !== 'disabled' || notionStatus !== 'disabled';

  // Runs the host's onImport and, if it returned created identities and this
  // entity kind has a fabric type, registers external identity + change events
  // (set 5B convergence). Backward-compatible: a void return registers nothing.
  async function runImport(rows: Record<string, unknown>[]) {
    const result = await onImport(rows);
    const fabricType = IMPORT_ENTITY_TO_FABRIC[entityType];
    if (Array.isArray(result) && result.length > 0 && fabricType) {
      const wsId = getCurrentWorkspaceId();
      const connectorType = notionModal ? 'notion' : miroModal ? 'miro' : 'import';
      if (wsId && connectorType !== 'import') {
        const records: ImportedRecordIdentity[] = result.map((r) => ({
          internalId: r.internalId,
          externalId: r.externalId,
          ...(r.externalUrl ? { externalUrl: r.externalUrl } : {}),
          ...(r.label ? { label: r.label } : {}),
        }));
        await registerImportedRecords({ workspaceId: wsId, entityType: fabricType, connectorType, records });
      }
    }
  }

  async function handleExcelImport(rows: Record<string, unknown>[]) {
    await runImport(rows);
    setShowImport(false);
  }

  async function handleMiroImport(rows: Record<string, unknown>[]) {
    await runImport(rows);
  }

  async function getExportData(): Promise<Record<string, unknown>[]> {
    if (getData) return getData();
    return data ?? [];
  }

  function openMiroImport() {
    setMiroExportRows(null);
    setMiroModal('import');
  }

  async function openMiroExport() {
    try {
      const rows = await getExportData();
      setMiroExportRows(rows);
      setMiroModal('export');
    } catch {
      setMiroExportRows([]);
      setMiroModal('export');
    }
  }

  function closeMiroModal() {
    setMiroModal(null);
    setMiroExportRows(null);
  }

  async function handleNotionImport(rows: Record<string, unknown>[]) {
    await runImport(rows);
  }

  function openNotionImport() {
    setNotionExportRows(null);
    setNotionModal('import');
  }

  async function openNotionExport() {
    try {
      const rows = await getExportData();
      setNotionExportRows(rows);
      setNotionModal('export');
    } catch {
      setNotionExportRows([]);
      setNotionModal('export');
    }
  }

  function closeNotionModal() {
    setNotionModal(null);
    setNotionExportRows(null);
  }

  return (
    <>
      <div className="bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          {/* Label */}
          <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] mr-1 shrink-0">
            <RefreshCw className="h-3.5 w-3.5" />
            Sync
          </span>

          <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:block">|</span>

          {/* Excel / CSV tile */}
          <div className="flex items-center gap-1.5">
            <FileSpreadsheet className="h-3.5 w-3.5 text-[var(--green)] shrink-0" />

            {/* Import */}
            <button
              onClick={() => setShowImport(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-[var(--green)] bg-[var(--bg-raised)] hover:bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--surface)] dark:border-[var(--green)] dark:text-[var(--text-ter)] dark:hover:bg-[var(--green)]/20"
            >
              <ArrowDownToLine className="h-3 w-3" />
              Import from Excel / CSV
            </button>

            {/* Download template */}
            <button
              onClick={() => downloadEntityTemplate(config, filename)}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface)] dark:hover:bg-[var(--bg-raised)] dark:text-[var(--text-sec)]"
              title="Download import template"
            >
              <Download className="h-3 w-3" />
              Template
            </button>

            {/* Export */}
            {!hideExport && (
              <InlineExportMenu data={data} getData={getData} filename={filename} />
            )}
          </div>

          {/* Connector tiles — shown based on workspace connector_configs */}
          {showConnectors && (
            <>
              <span className="text-[var(--text-ter)] dark:text-[var(--rule-bright)] hidden sm:block">|</span>
              <ConnectorTile
                icon={MIRO_SVG}
                label="Miro"
                status={miroStatus}
                onImport={miroStatus === 'active' ? openMiroImport : undefined}
                onExport={miroStatus === 'active' ? () => void openMiroExport() : undefined}
              />
              <ConnectorTile
                icon={JIRA_SVG}
                label="Jira"
                status={jiraStatus}
              />
              <ConnectorTile
                icon={NOTION_SVG}
                label="Notion"
                status={notionStatus}
                onImport={notionStatus === 'active' ? openNotionImport : undefined}
                onExport={notionStatus === 'active' ? () => void openNotionExport() : undefined}
              />
            </>
          )}
        </div>
      </div>

      {/* Excel import modal */}
      <ExcelImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onImport={handleExcelImport}
        appFields={config.appFields}
        title={config.label}
        description={config.description}
        validateRow={config.validateRow}
      />

      {/* Miro import/export modal */}
      {miroModal && (
        <MiroSyncModal
          mode={miroModal}
          onClose={closeMiroModal}
          onImport={handleMiroImport}
          exportData={miroExportRows ?? data ?? []}
          exportFilename={filename}
          entityType={entityType}
        />
      )}

      {/* Notion import/export modal */}
      {notionModal && (
        <NotionSyncModal
          mode={notionModal}
          onClose={closeNotionModal}
          onImport={handleNotionImport}
          exportData={notionExportRows ?? data ?? []}
        />
      )}
    </>
  );
}
